import { useEffect, useState } from "react";
import { doc, setDoc, orderBy, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useFirestoreCollection } from "../../lib/useFirestoreCollection";
import ImageUploader from "../../components/admin/ImageUploader.jsx";
import RichTextEditor from "../../components/admin/RichTextEditor.jsx";
import styles from "./ServicesPage.module.css";

/* Debe coincidir con los códigos/títulos de src/sections/Services.jsx */
const SERVICE_META = [
  { code: "01", title: "Diseño mecánico & fabricación" },
  { code: "02", title: "Importación de equipo de laboratorio" },
  { code: "03", title: "Electrónica, IoT & adquisición de datos" },
  { code: "04", title: "Automatización de procesos" },
  { code: "05", title: "Gemelos digitales" },
  { code: "06", title: "Retrofit & modernización" },
  { code: "07", title: "Diseño orientado a impresión 3D" },
  { code: "08", title: "Fabricación de componentes y equipamiento de pruebas de laboratorio" },
];

export default function ServicesPage() {
  const { items: detailDocs } = useFirestoreCollection("services");
  const detailMap = Object.fromEntries(detailDocs.map((d) => [d.id, d]));

  const [code, setCode] = useState("01");
  const current = SERVICE_META.find((s) => s.code === code);

  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const [error, setError] = useState("");

  // cargar el título/detalle guardado al cambiar de servicio
  useEffect(() => {
    setTitle(detailMap[code]?.title || current.title);
    setDetail(detailMap[code]?.detail || "");
    setStatus("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, detailDocs.length]);

  const { items: images } = useFirestoreCollection(
    `services/${code}/images`,
    orderBy("order")
  );

  async function save() {
    setStatus("saving");
    setError("");
    try {
      await setDoc(
        doc(db, "services", code),
        {
          title: title.trim() || current.title,
          detail: detail.trim(),
          order: Number(code),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setStatus("saved");
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  }

  return (
    <section style={{ display: "grid", gap: "1.5rem" }}>
      <header>
        <span className="mono-tag">// 05 — Servicios</span>
        <h1
          style={{
            fontSize: "var(--step-3)",
            fontWeight: 300,
            letterSpacing: "-0.02em",
            marginTop: "0.5rem",
          }}
        >
          Detalle de <strong>servicios</strong>
          <span style={{ color: "var(--color-accent)" }}>.</span>
        </h1>
        <p style={{ color: "var(--color-ink-soft)", marginTop: "0.5rem", maxWidth: "65ch" }}>
          Edita la explicación y las imágenes que se muestran en el panel de
          detalle de cada servicio (al hacer clic en “Detalles” en la web).
        </p>
      </header>

      <div className={styles.tabs}>
        {SERVICE_META.map((s) => (
          <button
            key={s.code}
            className={styles.tab}
            data-active={s.code === code}
            onClick={() => setCode(s.code)}
          >
            <span className={styles.tabCode}>{s.code}</span>
            {detailMap[s.code]?.title || s.title}
          </button>
        ))}
      </div>

      <div className={styles.editor}>
        <label className={styles.field}>
          <span className="mono-tag">// título del servicio</span>
          <input
            className={styles.titleInput}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={current.title}
          />
        </label>

        <div className={styles.field}>
          <span className="mono-tag">// explicación del servicio</span>
          <RichTextEditor
            value={detail}
            onChange={setDetail}
            placeholder="Describe en qué consiste este servicio, alcance, entregables…"
          />
        </div>

        <div className={styles.saveRow}>
          <button className={styles.save} onClick={save} disabled={status === "saving"}>
            {status === "saving" ? "Guardando…" : "Guardar explicación"}
          </button>
          {status === "saved" && <span className={styles.ok}>↳ guardado</span>}
          {status === "error" && <span className={styles.err}>// {error}</span>}
        </div>
      </div>

      <div>
        <span className="mono-tag" style={{ display: "block", marginBottom: "0.75rem" }}>
          // imágenes de {current.title}
        </span>
        <ImageUploader
          collectionPath={`services/${code}/images`}
          storageFolder={`services/${code}`}
          existing={images}
          onChange={() => {}}
        />
      </div>
    </section>
  );
}
